/**
 * SMTP Settings Admin Page
 *
 * Admin-only interface for configuring SMTP email settings.
 * Features:
 * - View current SMTP configuration
 * - Update SMTP settings with validation
 * - Test email functionality
 * - View audit log of configuration changes
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { adminApi } from '../../services/api';
import { useNavigate } from 'react-router-dom';

// Types
interface SMTPSettings {
  host: string;
  port: number;
  user: string;
  password: string;
  fromEmail: string;
  fromName: string;
  secure: boolean;
}

interface AuditLogEntry {
  id: number;
  userId: number;
  userEmail: string;
  action: string;
  details: any;
  ipAddress: string;
  createdAt: string;
}

interface SMTPSettingsResponse {
  isConfigured: boolean;
  settings: {
    host: string;
    port: number;
    user: string;
    passwordSet: boolean;
    fromEmail: string;
    fromName: string;
    secure: boolean;
  };
  lastUpdate: {
    updatedAt: string;
    updatedBy: string;
  } | null;
}

const SMTPSettings: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State
  const [settings, setSettings] = useState<SMTPSettings>({
    host: '',
    port: 587,
    user: '',
    password: '',
    fromEmail: '',
    fromName: '',
    secure: false,
  });
  const [isConfigured, setIsConfigured] = useState(false);
  const [passwordSet, setPasswordSet] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<{ updatedAt: string; updatedBy: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});


  // Fetch current settings
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response: SMTPSettingsResponse = await adminApi.getSMTPSettings();
      if (response) {
        setSettings({
          host: response.settings.host || '',
          port: response.settings.port || 587,
          user: response.settings.user || '',
          password: '', // Never returned from server
          fromEmail: response.settings.fromEmail || '',
          fromName: response.settings.fromName || '',
          secure: response.settings.secure || false,
        });
        setIsConfigured(response.isConfigured);
        setPasswordSet(response.settings.passwordSet);
        setLastUpdate(response.lastUpdate);
      }
    } catch (error: any) {
      console.error('Failed to fetch SMTP settings:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load SMTP settings',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch audit log
  const fetchAuditLog = useCallback(async () => {
    try {
      const response = await adminApi.getSMTPAuditLog(50, 0);
      if (response?.logs) {
        setAuditLog(response.logs);
      }
    } catch (error: any) {
      console.error('Failed to fetch audit log:', error);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (showAuditLog) {
      fetchAuditLog();
    }
  }, [showAuditLog, fetchAuditLog]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!settings.host.trim()) {
      newErrors.host = 'SMTP host is required';
    }

    if (!settings.port || settings.port < 1 || settings.port > 65535) {
      newErrors.port = 'Valid port (1-65535) is required';
    }

    if (!settings.user.trim()) {
      newErrors.user = 'SMTP username is required';
    }

    if (!passwordSet && !settings.password.trim()) {
      newErrors.password = 'Password is required for new configuration';
    }

    if (!settings.fromEmail.trim()) {
      newErrors.fromEmail = 'From email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(settings.fromEmail)) {
        newErrors.fromEmail = 'Invalid email format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save settings
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setMessage(null);

      const payload: any = {
        host: settings.host,
        port: settings.port,
        user: settings.user,
        fromEmail: settings.fromEmail,
        fromName: settings.fromName,
        secure: settings.secure,
      };

      // Only send password if it was changed
      if (settings.password.trim()) {
        payload.password = settings.password;
      }

      await adminApi.updateSMTPSettings(payload);

      setMessage({
        type: 'success',
        text: 'SMTP settings saved successfully!',
      });

      // Clear password field after save
      setSettings(prev => ({ ...prev, password: '' }));
      setPasswordSet(true);
      setIsConfigured(true);

      // Refresh settings
      fetchSettings();
    } catch (error: any) {
      console.error('Failed to save SMTP settings:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save settings',
      });
    } finally {
      setSaving(false);
    }
  };

  // Test email
  const handleTestEmail = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!testEmail.trim() || !emailRegex.test(testEmail)) {
      setMessage({
        type: 'error',
        text: 'Please enter a valid test email address',
      });
      return;
    }

    try {
      setTesting(true);
      setMessage(null);

      await adminApi.testSMTPSettings(testEmail);

      setMessage({
        type: 'success',
        text: `Test email sent successfully to ${testEmail}`,
      });
      setTestEmail('');
    } catch (error: any) {
      console.error('Failed to send test email:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send test email',
      });
    } finally {
      setTesting(false);
    }
  };

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value, 10) || 0 : value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">SMTP Email Configuration</h1>
          <p className="mt-2 text-gray-600">
            Configure the email server settings for system notifications, password resets, and alerts.
          </p>
        </div>

        {/* Status Card */}
        <div className={`mb-6 p-4 rounded-lg ${isConfigured ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="flex items-center">
            <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${isConfigured ? 'bg-green-100' : 'bg-yellow-100'}`}>
              {isConfigured ? (
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
            </div>
            <div className="ml-4">
              <h3 className={`text-lg font-medium ${isConfigured ? 'text-green-800' : 'text-yellow-800'}`}>
                {isConfigured ? 'SMTP Configured' : 'SMTP Not Configured'}
              </h3>
              <p className={`text-sm ${isConfigured ? 'text-green-600' : 'text-yellow-600'}`}>
                {isConfigured
                  ? 'Email functionality is active and ready to send.'
                  : 'Please configure SMTP settings to enable email functionality.'}
              </p>
              {lastUpdate && (
                <p className="text-xs text-gray-500 mt-1">
                  Last updated: {formatDate(lastUpdate.updatedAt)} by {lastUpdate.updatedBy}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Message Alert */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            <div className="flex">
              <div className="flex-shrink-0">
                {message.type === 'success' ? (
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">{message.text}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setMessage(null)}
                  className={`inline-flex rounded-md p-1.5 ${message.type === 'success' ? 'text-green-500 hover:bg-green-100' : 'text-red-500 hover:bg-red-100'}`}
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Settings Form */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-medium text-gray-900">SMTP Server Settings</h2>
          </div>
          <form onSubmit={handleSave} className="p-6 space-y-6">
            {/* Server Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="host" className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Host <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="host"
                  name="host"
                  value={settings.host}
                  onChange={handleChange}
                  placeholder="smtp.gmail.com"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.host ? 'border-red-300' : 'border-gray-300'}`}
                />
                {errors.host && <p className="mt-1 text-sm text-red-600">{errors.host}</p>}
              </div>

              <div>
                <label htmlFor="port" className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Port <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="port"
                  name="port"
                  value={settings.port}
                  onChange={handleChange}
                  placeholder="587"
                  min="1"
                  max="65535"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.port ? 'border-red-300' : 'border-gray-300'}`}
                />
                {errors.port && <p className="mt-1 text-sm text-red-600">{errors.port}</p>}
                <p className="mt-1 text-xs text-gray-500">Common ports: 25, 465 (SSL), 587 (TLS)</p>
              </div>
            </div>

            {/* Authentication */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="user" className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Username <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="user"
                  name="user"
                  value={settings.user}
                  onChange={handleChange}
                  placeholder="your-email@gmail.com"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.user ? 'border-red-300' : 'border-gray-300'}`}
                />
                {errors.user && <p className="mt-1 text-sm text-red-600">{errors.user}</p>}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Password {!passwordSet && <span className="text-red-500">*</span>}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={settings.password}
                    onChange={handleChange}
                    placeholder={passwordSet ? '(unchanged - enter new password to update)' : 'Enter password'}
                    className={`w-full px-3 py-2 pr-10 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.password ? 'border-red-300' : 'border-gray-300'}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                {passwordSet && <p className="mt-1 text-xs text-gray-500">Leave blank to keep current password</p>}
              </div>
            </div>

            {/* From Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="fromEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  From Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="fromEmail"
                  name="fromEmail"
                  value={settings.fromEmail}
                  onChange={handleChange}
                  placeholder="noreply@yourcompany.com"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.fromEmail ? 'border-red-300' : 'border-gray-300'}`}
                />
                {errors.fromEmail && <p className="mt-1 text-sm text-red-600">{errors.fromEmail}</p>}
              </div>

              <div>
                <label htmlFor="fromName" className="block text-sm font-medium text-gray-700 mb-1">
                  From Name
                </label>
                <input
                  type="text"
                  id="fromName"
                  name="fromName"
                  value={settings.fromName}
                  onChange={handleChange}
                  placeholder="Transportation Safety Platform"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Security */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="secure"
                name="secure"
                checked={settings.secure}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="secure" className="ml-2 block text-sm text-gray-700">
                Use SSL/TLS encryption
              </label>
              <span className="ml-2 text-xs text-gray-500">(Required for port 465, optional for 587)</span>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <button
                type="submit"
                disabled={saving}
                className={`inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {saving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Settings'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Test Email Section */}
        <div className="mt-6 bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-medium text-gray-900">Test Email Configuration</h2>
          </div>
          <div className="p-6">
            <p className="text-sm text-gray-600 mb-4">
              Send a test email to verify your SMTP configuration is working correctly.
            </p>
            <div className="flex gap-4">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter test email address"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={!isConfigured}
              />
              <button
                onClick={handleTestEmail}
                disabled={!isConfigured || testing || !testEmail.trim()}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${(!isConfigured || testing || !testEmail.trim()) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {testing ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Send Test Email
                  </>
                )}
              </button>
            </div>
            {!isConfigured && (
              <p className="mt-2 text-sm text-yellow-600">
                Please save your SMTP settings before testing.
              </p>
            )}
          </div>
        </div>

        {/* Audit Log Section */}
        <div className="mt-6 bg-white rounded-lg shadow-md overflow-hidden">
          <div
            className="px-6 py-4 border-b border-gray-200 bg-gray-50 cursor-pointer flex justify-between items-center"
            onClick={() => setShowAuditLog(!showAuditLog)}
          >
            <h2 className="text-lg font-medium text-gray-900">Configuration Audit Log</h2>
            <svg
              className={`w-5 h-5 text-gray-500 transform transition-transform ${showAuditLog ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          {showAuditLog && (
            <div className="p-6">
              {auditLog.length === 0 ? (
                <p className="text-sm text-gray-500">No audit log entries found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {auditLog.map((entry) => (
                        <tr key={entry.id}>
                          <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                            {formatDate(entry.createdAt)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">{entry.userEmail}</td>
                          <td className="px-4 py-2 text-sm">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              entry.action.includes('TEST') ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                            }`}>
                              {entry.action.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">
                            {entry.details && typeof entry.details === 'object' && 'host' in entry.details && <span>Host: {(entry.details as Record<string, unknown>).host as string}</span>}
                            {entry.details && typeof entry.details === 'object' && 'testEmail' in entry.details && <span>To: {(entry.details as Record<string, unknown>).testEmail as string}</span>}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500">{entry.ipAddress}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SMTPSettings;
